import React, { useCallback, useEffect, useState } from 'react'
import { Button, Grid, Icon, Select, TextInput } from 'gu-ui-kit'
import { NumericFormat } from 'react-number-format'
import PropTypes from 'prop-types'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/subcomponents/ControlsPanel/controls-panel.module.scss'
import controlsStyles from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/subcomponents/ControlsPanel/subcomponents/BudgetControls/budget-controls.module.scss'
import clsx from 'clsx'
import { MODIFY_BUDGETS_BY_RULE } from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Decimal from 'decimal.js'

const BudgetControls = ({ submitting, error, touched, ...rest }) => {
  const breakpoints = useBreakpoint()
  const { md } = useBreakpoint()
  const sm = !md
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [amount, setAmount] = useState()
  const [budgetType, setBudgetType] = useState('budget_limit')
  const [modificationType, setModificationType] = useState('set')
  const [isPristine, setIsPristine] = useState(true)

  useEffect(() => {
    setIsPristine(!amount)
  }, [budgetType, modificationType, amount])

  const modifyRows = useCallback(() => {
    dispatch({
      type: MODIFY_BUDGETS_BY_RULE,
      payload: { modificationType, budgetType, amount },
    })
    setIsPristine(true)
  }, [setIsPristine, modificationType, budgetType, amount])

  return (
    <Grid
      gap={16}
      wrap={sm}
      className={clsx('margin-bottom-32', {
        [styles['fullWidth']]: sm,
      })}
    >
      <Select
        className={controlsStyles['select']}
        fullWidth={sm}
        label={''}
        size={'m'}
        showEmptyOption={false}
        value={modificationType}
        onChange={setModificationType}
        options={['set', 'increase', 'decrease'].map((type) => ({
          value: type,
          label: t('campaigns_editor::budget_editor.controls_panel.budget.action_type', {
            context: type,
          }),
        }))}
      />
      <NumericFormat
        value={amount}
        onChange={({ currentTarget: { value } }) => {
          setAmount(value === '' ? null : parseFloat(value.trim().replace(/-|\s/g, '')))
        }}
        isAllowed={({ value }) => {
          if (!value) {
            return true
          } else {
            const floatVal = new Decimal(value)
            return floatVal.greaterThan('0.00') && floatVal.lessThanOrEqualTo('1000000000.00')
          }
        }}
        fullWidth={sm}
        placeholder={t('campaigns_editor::budget_editor.controls_panel.budget.amount.placeholder')}
        customInput={TextInput}
        className={styles['amount-field']}
        thousandSeparator={' '}
        allowLeadingZeros={true}
        decimalScale={2}
        decimalSeparator={'.'}
        fixedDecimalScale
        disabled={submitting || rest.disabled}
        helperText={touched && error ? rest.t(error) : rest.helperText}
        error={!!(touched && error)}
        size={'m'}
        suffix={' ₽'}
      />
      <Select
        style={{ maxWidth: '200px' }}
        fullWidth={sm}
        size={'m'}
        showEmptyOption={false}
        value={budgetType}
        onChange={setBudgetType}
        options={['budget_limit', 'budget_limit_day'].map((limit) => ({
          value: limit,
          label: t('campaigns_editor::budget_editor.controls_panel.budget.limit_type', {
            context: limit,
          }),
        }))}
      />
      <Button
        noLabel={!breakpoints.fb && breakpoints.md}
        fullWidth={sm}
        size={'m'}
        variant={'secondary'}
        disabled={isPristine}
        startIcon={<Icon name={'check'} />}
        onClick={modifyRows}
      >
        {t('common::buttons.apply')}
      </Button>
    </Grid>
  )
}

export default BudgetControls

BudgetControls.propTypes = {
  submitting: PropTypes.bool,
  error: PropTypes.string,
  touched: PropTypes.bool,
}
